/**
 * Kiểm soát nội bộ bước 5 (đề xuất v2 "Tab Nhật ký: ai thêm, ai sửa, ai xóa", DEC-278): nhóm thao tác + diễn giải
 * trước / sau cho màn tra nhật ký ERP. Không đổi cách ghi audit_logs (chỉ đọc).
 */

/** Loại chứng từ / danh mục nghiệp vụ người Kiểm soát (control.check, không có audit.read) được tra. HR, lương, tài khoản, AI: không. */
export const AUDIT_BUSINESS_ENTITY_LABELS: Record<string, string> = {
  quote: 'Báo giá',
  sales_order: 'Đơn bán',
  contract: 'Hợp đồng',
  control_check: 'Kiểm soát đơn chốt',
  customer: 'Khách hàng',
  customer_price: 'Giá riêng khách',
  product: 'Mã hàng',
  product_uom: 'ĐVT mã hàng',
  price_list: 'Bảng giá',
  price_list_item: 'Dòng bảng giá',
  purchase_request: 'Đề nghị mua',
  purchase_rfq: 'Hỏi giá NCC',
  purchase_order: 'Đơn mua',
  grn: 'Nhận hàng mua',
  supplier: 'Nhà cung cấp',
  delivery_note: 'Phiếu giao',
  delivery_trip: 'Chuyến giao',
  stock_document: 'Phiếu kho',
  stock_count: 'Kiểm kho',
  reservation: 'Giữ hàng',
  tender: 'Hồ sơ thầu',
  debt_confirmation: 'Đối chiếu công nợ',
  payment: 'Thanh toán',
  approval_request: 'Yêu cầu duyệt',
};
export const AUDIT_BUSINESS_ENTITIES = Object.keys(AUDIT_BUSINESS_ENTITY_LABELS);

export const AUDIT_ACTION_GROUPS = ['create', 'update', 'price_qty', 'delete', 'cancel', 'status', 'asia'] as const;
export type AuditActionGroup = (typeof AUDIT_ACTION_GROUPS)[number];
export const AUDIT_ACTION_GROUP_LABELS: Record<AuditActionGroup, string> = {
  create: 'Thêm',
  update: 'Sửa',
  price_qty: 'Đổi giá / số lượng',
  delete: 'Xóa',
  cancel: 'Hủy',
  status: 'Đổi trạng thái / duyệt',
  asia: 'Đồng bộ / ghi Asia',
};
/** Mã thao tác (audit_logs.action) theo nhóm; "Đổi giá / SL" lọc theo nội dung thay đổi, không theo mã. */
export const AUDIT_ACTIONS_BY_GROUP: Record<Exclude<AuditActionGroup, 'price_qty'>, string[]> = {
  create: ['create', 'created', 'create_manual', 'manual_create', 'create_temp', 'sync_create', 'import', 'import_commit', 'quote_from_boq'],
  update: ['update', 'updated', 'manual_edit', 'update_profile', 'boq_line_update', 'rate_change', 'credit_limit_change', 'map_temp', 'merge', 'assign'],
  delete: ['delete', 'document_delete', 'archive'],
  cancel: ['cancel', 'approval_cancel', 'void_receipt', 'import_cancel', 'reject'],
  status: ['status', 'status_change', 'stage_change', 'approve', 'auto_approve', 'approval_self', 'confirm', 'convert', 'close', 'complete', 'reopen', 'send', 'sign'],
  asia: ['asia_sync', 'asia_write', 'asia_write_send', 'asia_write_approve', 'asia_changed', 'asia_conflict', 'asia_conflict_resolve', 'asia_link', 'sync_update', 'match_asia', 'auto_map_temp', 'sync_unlock', 'asia_sync_force'],
};
/** Khóa trong `changes` coi là đổi giá / số lượng / tiền. */
export const AUDIT_PRICE_QTY_KEYS = ['items', 'unit_price', 'price', 'quantity', 'qty', 'grand_total', 'total', 'discount_percent', 'list_price', 'cost_price'];

export function auditActionGroup(action: string): AuditActionGroup | null {
  for (const [g, list] of Object.entries(AUDIT_ACTIONS_BY_GROUP)) if (list.includes(action)) return g as AuditActionGroup;
  return null;
}

type Change = { from: unknown; to: unknown };
const show = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '(trống)';
  if (typeof v === 'number') return new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 3 }).format(v);
  if (typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v)) return show(Number(v));
  if (typeof v === 'object') return JSON.stringify(v).slice(0, 120);
  return String(v);
};
const LINE_FIELD: Record<string, string> = { qty: 'SL', price: 'giá', disc: 'CK %', vat: 'VAT %', unit: 'ĐVT', factor: 'hệ số', list: 'giá bảng' };
type Line = Record<string, unknown> & { line?: unknown; product?: unknown };
const isLines = (v: unknown): v is Line[] => Array.isArray(v) && v.every((x) => x && typeof x === 'object' && 'line' in (x as object));
const keyOf = (l: Line) => `${String(l.product ?? '')}#${String(l.line ?? '')}`;

/** Diễn giải dòng hàng trước → sau (báo giá, đơn): thêm / bỏ / sửa SL, giá… theo mã + số dòng. */
export function describeLineChanges(from: Line[], to: Line[]): string[] {
  const out: string[] = [];
  const before = new Map(from.map((l) => [keyOf(l), l]));
  const after = new Map(to.map((l) => [keyOf(l), l]));
  // Ghép theo mã khi số dòng dịch (thêm / bỏ dòng ở giữa)
  const byProduct = (m: Map<string, Line>, l: Line) => [...m.values()].find((x) => String(x.product) === String(l.product));
  const used = new Set<Line>();
  for (const a of to) {
    const b = before.get(keyOf(a)) ?? byProduct(before, a);
    if (!b || used.has(b)) {
      out.push(`Thêm dòng ${String(a.product ?? '')}: SL ${show(a.qty)} × ${show(a.price)}`);
      continue;
    }
    used.add(b);
    const fields = Object.keys(LINE_FIELD).filter((f) => JSON.stringify(a[f] ?? null) !== JSON.stringify(b[f] ?? null));
    if (fields.length) out.push(`Sửa ${String(a.product ?? '')}: ${fields.map((f) => `${LINE_FIELD[f]} ${show(b[f])} → ${show(a[f])}`).join(', ')}`);
  }
  for (const b of from) if (!used.has(b) && !after.has(keyOf(b))) out.push(`Bỏ dòng ${String(b.product ?? '')} (SL ${show(b.qty)})`);
  return out;
}

/** Diễn giải `changes` của 1 dòng nhật ký thành câu trước → sau (tối đa 20 dòng). */
export function describeAuditChanges(changes: Record<string, Change> | null | undefined): string[] {
  if (!changes) return [];
  const out: string[] = [];
  for (const [k, c] of Object.entries(changes)) {
    if (!c || typeof c !== 'object' || !('from' in c)) continue;
    if (isLines(c.from) || isLines(c.to)) out.push(...describeLineChanges(isLines(c.from) ? c.from : [], isLines(c.to) ? c.to : []));
    else out.push(`${k}: ${show(c.from)} → ${show(c.to)}`);
  }
  return out.slice(0, 20);
}
